import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';

export const tripPlannerEntryPointParamKey = 'entryPoint';
export const tripPlannerEntryLocationIdParamKey = 'entryLocationId';
export const tripPlannerEntryParkIdParamKey = 'entryParkId';

export type TripPlannerEntryPoint = Extract<
  CardLocation,
  | 'explore side bar'
  | 'location page'
  | 'park page'
  | 'poi page'
  | 'search result'
  | 'web explore nav menu'
  | 'web homepage button group below search bar'
>;

const tripPlannerEntryPoints: readonly TripPlannerEntryPoint[] = [
  'explore side bar',
  'location page',
  'park page',
  'poi page',
  'search result',
  'web explore nav menu',
  'web homepage button group below search bar'
];

export type TripPlannerEntryContext = {
  entryPoint?: TripPlannerEntryPoint;
  locationId?: number;
  parkId?: number;
};

export const getTripPlannerEntryParams = ({ entryPoint, locationId, parkId }: TripPlannerEntryContext): Record<string, string> => ({
  ...(entryPoint ? { [tripPlannerEntryPointParamKey]: entryPoint } : {}),
  ...(locationId === undefined ? {} : { [tripPlannerEntryLocationIdParamKey]: String(locationId) }),
  ...(parkId === undefined ? {} : { [tripPlannerEntryParkIdParamKey]: String(parkId) })
});

/**
 * Builds a link into the trip planner, carrying the attribution params the destination parses back out.
 * The locale prefix keeps a non en-US visitor on their own language rather than dropping them on the English page.
 */
export const getTripPlannerHref = ({ languageRegionCode, ...context }: TripPlannerEntryContext & { languageRegionCode: LanguageRegionCode }) =>
  wrapUrlSafe(`/trip-assistant?${new URLSearchParams(getTripPlannerEntryParams(context))}`, languageRegionCode);

/** Where the trip planner row in a search dropdown points - shared by the homepage, the nav header and explore. */
export const getTripPlannerSearchResultHref = (languageRegionCode: LanguageRegionCode) =>
  getTripPlannerHref({ entryPoint: 'search result', languageRegionCode });

const parseId = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
};

export const parseTripPlannerEntryContext = (getParam: (key: string) => string | null | undefined): TripPlannerEntryContext => {
  const rawEntryPoint = getParam(tripPlannerEntryPointParamKey);
  return {
    entryPoint: tripPlannerEntryPoints.find(entryPoint => entryPoint === rawEntryPoint),
    locationId: parseId(getParam(tripPlannerEntryLocationIdParamKey)),
    parkId: parseId(getParam(tripPlannerEntryParkIdParamKey))
  };
};
